import React, { useEffect, useMemo, useState } from 'react';
import { X, History, Loader2, ExternalLink } from 'lucide-react';
import { userService } from '../../services/api';
import { parseUserHistory, HistoryEvent, VERIFIED_SUFFIX } from '../../utils/userHistory';
import { formatDateToDdmmyyyy } from '../../utils/format';

// A user's change history, laid out like the API response: one card per event
// (one API call) with who made it and when on top, then each changed field with
// its old and new value.

type Section = 'basic' | 'kyc' | 'bank' | 'verification' | 'other';

const FIELD_LABELS: Record<string, string> = {
    first_name: 'First Name', last_name: 'Last Name', name: 'Name', email: 'Email',
    country_code: 'Country Code', mobile: 'Mobile', role: 'Role', refered_by_mobile: 'Referred By',
    imageUrl: 'Profile Photo',
    dob: 'Date of Birth', gender: 'Gender', occupation: 'Occupation', aadhar_number: 'Aadhaar Number',
    aadharCardNumber: 'Aadhaar Number', panCardNumber: 'PAN Number', pan_number: 'PAN Number',
    panCardUrl: 'PAN Card Image', aadhar_address: 'Aadhaar Address', permanent_address: 'Permanent Address',
    address: 'Permanent Address', current_address: 'Current Address', city: 'City', state: 'State',
    pincode: 'Pincode', aadhar_front_image_url: 'Aadhaar Front Image', aadhar_back_image_url: 'Aadhaar Back Image',
    verified: 'KYC Verified', kyc_status: 'KYC Status', customer_remarks: 'Customer Remarks',
    acknowledgment_link: 'Acknowledgment Link', kyc_form: 'KYC Form',
    account_holder_name: 'Account Holder Name', account_number: 'Account Number', ifsc_code: 'IFSC Code',
    bank_name: 'Bank Name', passbook_image_url: 'Passbook Photo', cancelled_cheque_url: 'Cancelled Cheque Photo',
    doc_aadhar_front: 'Aadhaar Front Image', doc_aadhar_back: 'Aadhaar Back Image', doc_pan_card: 'PAN Card Image',
    doc_bank_passbook: 'Passbook Photo', doc_bank_cheque: 'Cancelled Cheque Photo',
};

const SECTION_OF: Record<string, Section> = {
    first_name: 'basic', last_name: 'basic', name: 'basic', email: 'basic', country_code: 'basic',
    mobile: 'basic', role: 'basic', refered_by_mobile: 'basic', imageUrl: 'basic',
    dob: 'kyc', gender: 'kyc', occupation: 'kyc', aadhar_number: 'kyc', aadharCardNumber: 'kyc',
    panCardNumber: 'kyc', panCardUrl: 'kyc', aadhar_address: 'kyc', permanent_address: 'kyc', address: 'kyc',
    current_address: 'kyc', city: 'kyc', state: 'kyc', pincode: 'kyc', aadhar_front_image_url: 'kyc',
    aadhar_back_image_url: 'kyc', verified: 'kyc',
    account_holder_name: 'bank', account_number: 'bank', ifsc_code: 'bank', bank_name: 'bank',
    passbook_image_url: 'bank', cancelled_cheque_url: 'bank',
    kyc_status: 'verification', customer_remarks: 'verification',
    acknowledgment_link: 'verification', kyc_form: 'verification',
};

const sectionOf = (field: string): Section =>
    field.endsWith(VERIFIED_SUFFIX) || field.endsWith('_correction_remarks') ? 'verification' : SECTION_OF[field] || 'other';

const labelOf = (field: string): string => {
    if (field.endsWith(VERIFIED_SUFFIX)) {
        const base = field.slice(0, -VERIFIED_SUFFIX.length);
        return `${FIELD_LABELS[base] || base.replace(/_/g, ' ')} Verification`;
    }
    return FIELD_LABELS[field] || field.replace(/_/g, ' ');
};

const FILTERS: Array<{ id: 'all' | Section; label: string }> = [
    { id: 'all', label: 'All' }, { id: 'basic', label: 'Basic' }, { id: 'kyc', label: 'KYC' },
    { id: 'bank', label: 'Bank' }, { id: 'verification', label: 'Verification' },
];

const isUrl = (v: string) => /^https?:\/\//i.test(v);

const Value: React.FC<{ value: string | null; field: string }> = ({ value, field }) => {
    if (value === null || value === undefined || value === '') {
        return <span className="text-slate-400 italic">empty</span>;
    }
    let text = String(value);
    if (field === 'verified') text = /^true$/i.test(text) ? 'Yes' : 'No';
    if (isUrl(text)) {
        return (
            <a href={text} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#1B4332] font-semibold hover:underline">
                View image <ExternalLink size={12} />
            </a>
        );
    }
    return <span className="font-semibold text-slate-900 break-words [overflow-wrap:anywhere]">{text}</span>;
};

const dateTimeOf = (ts?: string) => {
    const d = ts ? new Date(ts) : null;
    if (!d || isNaN(d.getTime())) return { date: '', time: '' };
    return {
        date: formatDateToDdmmyyyy(d),
        time: d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
    };
};

const isCustomer = (e: HistoryEvent) => e.by === 'customer' || (!e.by && !e.by_name);


export interface UserHistoryModalProps {
    mobile: string;
    userName?: string;
    onClose: () => void;
}

const UserHistoryModal: React.FC<UserHistoryModalProps> = ({ mobile, userName, onClose }) => {
    const [events, setEvents] = useState<HistoryEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<'all' | Section>('all');

    // Always re-read on open so the latest changes show.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        userService.getUserDetails(mobile)
            .then((res: any) => {
                if (cancelled) return;
                setEvents(parseUserHistory(res?.user || res));
            })
            .catch(() => !cancelled && setError('Could not load history. Please try again.'))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [mobile]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Newest first.
    const sorted = useMemo(() => events
        .slice()
        .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()),
    [events]);

    const counts = useMemo(() => {
        const c: Record<string, number> = { all: sorted.length };
        sorted.forEach(e => new Set(e.changes.map(ch => sectionOf(ch.field))).forEach(s => { c[s] = (c[s] || 0) + 1; }));
        return c;
    }, [sorted]);

    // A section filter keeps only that section's changes.
    const shown = useMemo(() => sorted
        .map(e => ({ ...e, changes: filter === 'all' ? e.changes : e.changes.filter(c => sectionOf(c.field) === filter) }))
        .filter(e => e.changes.length > 0),
    [sorted, filter]);

    return (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4"
            onClick={onClose}>
            <div role="dialog" aria-modal="true" aria-labelledby="user-history-title"
                className="bg-white w-full sm:max-w-2xl max-h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-slate-100">
                    <div className="flex items-start gap-3 min-w-0">
                        <span className="w-10 h-10 rounded-xl bg-[#f0f9f4] text-[#4a7c59] flex items-center justify-center shrink-0">
                            <History size={18} />
                        </span>
                        <div className="min-w-0">
                            <h2 id="user-history-title" className="text-base font-bold text-slate-900">Change History</h2>
                            <p className="text-xs text-slate-500 mt-0.5 truncate">
                                {userName ? `${userName} · ` : ''}Every update to this user's details
                            </p>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close"
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
                        <X size={16} />
                    </button>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-1.5 px-6 py-3 border-b border-slate-100 overflow-x-auto">
                    {FILTERS.map(f => {
                        const active = filter === f.id;
                        return (
                            <button key={f.id} type="button" onClick={() => setFilter(f.id)}
                                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${active
                                    ? 'bg-[#1B4332] text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                                {f.label}
                                <span className={`tabular-nums ${active ? 'text-white/80' : 'text-slate-400'}`}>{counts[f.id] || 0}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Timeline */}
                <div className="flex-1 overflow-y-auto px-6 py-5 bg-slate-50/50">
                    {loading ? (
                        <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
                            <Loader2 size={16} className="animate-spin" /> Loading history…
                        </div>
                    ) : error ? (
                        <p className="py-16 text-center text-sm text-red-600">{error}</p>
                    ) : shown.length === 0 ? (
                        <div className="py-16 text-center">
                            <History size={22} className="mx-auto text-slate-300" />
                            <p className="mt-2 text-sm font-semibold text-slate-700">No history yet</p>
                            <p className="text-xs text-slate-500 mt-0.5">Changes to this user's details will appear here.</p>
                        </div>
                    ) : (
                        <ol className="space-y-4">
                            {shown.map((e, idx) => {
                                const customer = isCustomer(e);
                                const name = customer ? (e.by_name || 'Customer') : (e.by_name || e.by || 'Unknown');
                                const { date, time } = dateTimeOf(e.timestamp);
                                return (
                                    <li key={`${e.timestamp}-${idx}`} className="bg-white rounded-xl ring-1 ring-slate-200/80 overflow-hidden">
                                        {/* Who and when */}
                                        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-100">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <span className="w-9 h-9 rounded-full bg-[#f0f9f4] text-[#4a7c59] ring-1 ring-[#4a7c59]/20 flex items-center justify-center text-sm font-bold shrink-0">
                                                    {name.trim()[0]?.toUpperCase() || '?'}
                                                </span>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold text-slate-900 truncate">{name}</p>
                                                    <p className="text-xs text-slate-500 truncate">
                                                        {customer ? 'Customer' : [e.by, e.by_role].filter(Boolean).join(' · ') || '—'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="text-xs font-semibold text-slate-700">{date}</p>
                                                <p className="text-xs text-slate-500 tabular-nums">{time}</p>
                                            </div>
                                        </div>

                                        {/* What changed */}
                                        <table className="w-full text-[13px]">
                                            <thead>
                                                <tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                                    <th className="px-4 py-2 w-[34%]">Field</th>
                                                    <th className="px-4 py-2 w-[33%]">Old Value</th>
                                                    <th className="px-4 py-2 w-[33%]">New Value</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {e.changes.map((c, i) => (
                                                    <tr key={`${c.field}-${i}`} className="align-top">
                                                        <td className="px-4 py-2.5 font-medium text-slate-700 capitalize">{labelOf(c.field)}</td>
                                                        <td className="px-4 py-2.5 bg-red-50/40"><Value value={c.old_value} field={c.field} /></td>
                                                        <td className="px-4 py-2.5 bg-emerald-50/40"><Value value={c.new_value} field={c.field} /></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserHistoryModal;
